// JobNotifierContext — app-root watcher that surfaces pipeline-job completion
// notifications ACROSS the whole SPA.
// -----------------------------------------------------------------
// The stage panels (<TTSPanel> / <PipelinePanel> / <ProductionTourView>) render
// the LIVE progress bar for a running `pipeline_jobs/<id>` via the per-plan
// <PipelineJobProvider>. But that provider unmounts the moment the operator
// navigates away from the plan editor, so the "job complete / failed" toast
// (which fires on the running → terminal transition) was never seen for a job
// that finished on another screen. The START toast survives (Mantine's store is
// global); the COMPLETION toast did not. That made start-only feedback useless:
// the whole point is "I launch a long job, go do something else, and get pinged
// when it's done".
//
// This provider lives at the ROOT (mounted in routes/__root.tsx, inside
// <AuthProvider>, above <Outlet />), so it OUTLIVES every screen. Any surface
// that dispatches a job calls `registerJob(jobId)`; the provider attaches one
// listener per tracked job (rendered as a <JobWatcher/> child so each gets its
// own `useFirestoreDoc` at a stable hook position), fires the shared
// `announceJobTerminal` (tier A in-app toast + tier B backgrounded-tab OS
// notification) on completion, then de-registers so the listener detaches.
//
// SINGLE OWNER: the per-plan <PipelineJobProvider> feeds this provider off its
// `activeJobId` (covering in-session dispatch AND reattach-after-reload) and the
// inline per-plan terminal toast was removed, so a job is announced exactly
// once whether or not the operator is still on the launching screen.
//
// Scope: this owns the `pipeline_jobs/<id>` bulk/produce jobs (the long fan-outs
// where leave-and-come-back matters). Single-track artwork/transition worker
// completion is surfaced by `useWorkerAction`'s execution-channel toast (editor-
// scoped); the room-crawler / tour-curator use their own `producer_jobs` system.
// Those can be folded in later via the same `registerJob` if desired.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useFirestoreDoc } from '@/hooks/useFirestoreDoc';
import { announceJobTerminal } from '@/lib/pipeline/jobTerminalToast';
import { type PipelineJob } from '@/lib/types/pipelineJob';

export type JobNotifierValue = {
  /** Track a `pipeline_jobs/<jobId>` doc for a cross-screen completion
   *  notification. Idempotent — registering an already-tracked id is a no-op.
   *  Call it right after a dispatch that returned a `job_id` (the job is
   *  running), or whenever a running job id is (re)discovered. */
  registerJob: (jobId: string) => void;
};

const JobNotifierContextImpl = createContext<JobNotifierValue | null>(null);

type Props = { children: ReactNode };

/**
 * One live listener on a single tracked `pipeline_jobs/<jobId>` doc. Fires the
 * shared terminal notification once (on the first terminal snapshot), then
 * asks the provider to drop it (which unmounts this watcher → detaches the
 * listener). Also self-drops if the doc resolves ABSENT after loading (a bad /
 * TTL-reaped id) so a listener can't leak forever.
 */
const JobWatcher = ({
  jobId,
  onDone,
}: {
  jobId: string;
  onDone: (jobId: string) => void;
}) => {
  const { data: job, loading } = useFirestoreDoc<PipelineJob>(
    `pipeline_jobs/${jobId}`,
  );
  const firedRef = useRef(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;

    const status = job?.status;

    // Terminal → announce once, then de-register.
    if (status && status !== 'running') {
      firedRef.current = true;
      announceJobTerminal(job, jobId);
      onDone(jobId);
      return;
    }

    // Absent-doc guard: once the listener has actually loaded (so this isn't
    // the just-registered gap where `loading` hasn't flipped yet) and resolved
    // to no doc, drop the tracker so the listener doesn't linger.
    if (loading) {
      loadedRef.current = true;
      return;
    }
    if (loadedRef.current && job === undefined) {
      onDone(jobId);
    }
  }, [job, loading, jobId, onDone]);

  return null;
};

/**
 * App-root provider. Mount once in `routes/__root.tsx` inside <AuthProvider>.
 */
export const JobNotifierProvider = ({ children }: Props) => {
  const [jobIds, setJobIds] = useState<ReadonlySet<string>>(new Set());

  const registerJob = useCallback((jobId: string) => {
    if (!jobId) return;
    setJobIds((prev) => {
      if (prev.has(jobId)) return prev;
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  }, []);

  const unregisterJob = useCallback((jobId: string) => {
    setJobIds((prev) => {
      if (!prev.has(jobId)) return prev;
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
  }, []);

  const value = useMemo<JobNotifierValue>(() => ({ registerJob }), [
    registerJob,
  ]);

  return (
    <JobNotifierContextImpl.Provider value={value}>
      {children}
      {[...jobIds].map((id) => (
        <JobWatcher key={id} jobId={id} onDone={unregisterJob} />
      ))}
    </JobNotifierContextImpl.Provider>
  );
};

/**
 * Access `registerJob`. Returns a NO-OP when no provider is mounted (e.g. a
 * component test that doesn't wrap in <JobNotifierProvider>) so consumers stay
 * decoupled from test harness wiring — the notifier is an additive, non-load-
 * bearing side channel, not a correctness dependency.
 */
export const useJobNotifier = (): JobNotifierValue => {
  const ctx = useContext(JobNotifierContextImpl);
  return ctx ?? NOOP_VALUE;
};

const NOOP_VALUE: JobNotifierValue = { registerJob: () => {} };
